import { Skeleton } from '@/components/ui/skeleton';
import { ITrending } from '@/request/token/token.response.interface';
import { useDynamicHeight } from '@/hooks/base/useDynamicHeight';
import NoData from '@/widget/NoData';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef } from 'react';
import TokenRow from './TokenRow';
import { useTailwindRealHeight } from '@/hooks/base/useTailwindRealHeight';

interface IProps {
  sortedTokens: ITrending[];
  loading: boolean;
}

export default (props: IProps) => {
  const { sortedTokens, loading } = props;
  const height = useDynamicHeight();
  const parentRef = useRef<HTMLDivElement>(null);
  const tailwindRealHeight = useTailwindRealHeight(64);

  const rowVirtualizer = useVirtualizer({
    count: sortedTokens.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => tailwindRealHeight,
    overscan: 5,
  });

  useEffect(() => {
    if (parentRef.current) {
      rowVirtualizer.measure();
    }
  }, [height, tailwindRealHeight, parentRef.current]);

  return loading ? (
    <div>
      {[...Array(10)].map((_, index) => {
        return (
          <div className="h-[64px] flex items-center border-b-[1px] border-borderColor" key={index}>
            <div className="flex-1">
              <Skeleton className="h-[42px] w-[80%] rounded-xl" />
            </div>
            <div className="flex-[0.5]">
              <Skeleton className="h-[42px] w-full rounded-xl" />
            </div>
            <div className="flex-[0.6] flex  justify-end">
              <Skeleton className="h-[42px] w-[80%] rounded-xl" />
            </div>
          </div>
        );
      })}
    </div>
  ) : sortedTokens.length > 0 ? (
    <div
      ref={parentRef}
      style={{
        height: height + 'px',
        overflow: 'auto',
      }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => (
          <div
            key={virtualRow.index}
            style={{
              position: 'absolute',
              width: '100%',
              height: `${tailwindRealHeight}px`,
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            <TokenRow token={sortedTokens[virtualRow.index]} />
          </div>
        ))}
      </div>
    </div>
  ) : (
    <NoData />
  );
};
